import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, MapPin, MessageSquare, UserPlus, UserCheck, FileText, Globe, Languages, Clock, Users, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Loader, UserAvatar, SpecPill, AvailabilityPill, CertList, Pill, VipBadge } from "@/components/common";
import JobCard from "@/components/JobCard";
import { useApi, errMsg } from "@/lib/api";
import { useMe } from "@/context/MeContext";

export function CvButton({ path, filename }) {
  const api = useApi();
  const open = async () => {
    try {
      const blob = await api("get", `/files/${path}`, null, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = filename || "cv.pdf"; a.click();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return <Button variant="outline" onClick={open} data-testid="profile-cv-download"><FileText size={16} />Download CV</Button>;
}

const Section = ({ title, children }) => (
  <section className="panel p-6"><h2 className="eyebrow mb-4">{title}</h2>{children}</section>
);

export function ProfileView({ p, onEndorse }) {
  const isCompany = p.role === "company";
  return (
    <div className="grid lg:grid-cols-12 gap-4">
      <div className="lg:col-span-8 space-y-4">
        <Section title={isCompany ? "About the company" : "About me"}>
          <p className="text-slate-700 whitespace-pre-line leading-relaxed">{p.bio || "No description."}</p>
        </Section>
        {!isCompany && (
          <Section title="Experience">
            {(p.experience || []).length === 0 ? <p className="text-sm text-slate-500">No entries.</p> : (
              <ol className="relative border-l border-slate-200 ml-1 space-y-6">
                {p.experience.map((x, i) => (
                  <li key={`${x.company}-${x.position}-${x.period}-${i}`} className="pl-5 relative">
                    <span className="absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full bg-sky-600" />
                    <p className="font-bold">{x.position}</p>
                    <p className="text-sm text-slate-600">{x.company} <span className="font-mono text-xs text-slate-400 ml-1">{x.period}</span></p>
                    {x.description && <p className="mt-1 text-sm text-slate-700">{x.description}</p>}
                  </li>
                ))}
              </ol>
            )}
          </Section>
        )}
      </div>
      {!isCompany && (
        <div className="lg:col-span-4 space-y-4">
          <Section title="Certificates"><CertList certs={p.certificates} /></Section>
          <Section title="Skills">
            {(p.skills || []).length === 0 ? <span className="text-sm text-slate-500">—</span> : (
              <ul className="space-y-1.5">
                {p.skills.map((s) => {
                  const n = p.endorsements?.counts?.[s] || 0;
                  const mine = p.endorsements?.mine?.includes(s);
                  return (
                    <li key={s} className="flex items-center justify-between gap-2" data-testid={`skill-${s}`}>
                      <span className="text-sm font-medium text-slate-800">{s}</span>
                      <span className="flex items-center gap-1.5">
                        {n > 0 && <span className="font-mono text-[11px] text-sky-700" data-testid={`skill-count-${s}`}>{n} endorsement{n === 1 ? "" : "s"}</span>}
                        {onEndorse && (
                          <Button size="sm" variant={mine ? "default" : "outline"} className="h-7 px-2 text-xs" onClick={() => onEndorse(s)} data-testid={`skill-endorse-${s}`}>
                            <ThumbsUp size={12} />{mine ? "Endorsed" : "Endorse"}
                          </Button>
                        )}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
          <Section title="Turbines">
            <div className="flex flex-wrap gap-1.5">{(p.turbines || []).length ? p.turbines.map((s) => <Pill key={s} cls="bg-amber-50 text-amber-800 border-amber-200">{s}</Pill>) : <span className="text-sm text-slate-500">—</span>}</div>
          </Section>
        </div>
      )}
    </div>
  );
}

export function ProfileHeader({ p, actions }) {
  return (
    <div className="panel p-6 sm:p-8 flex flex-col sm:flex-row gap-6 sm:items-center relative overflow-hidden">
      <div className="absolute right-0 top-0 h-full w-1.5 bg-gradient-to-b from-sky-500 to-amber-400" />
      <UserAvatar user={p} size={96} testId="profile-avatar" />
      <div className="flex-1 min-w-0">
        <p className="eyebrow" data-testid="profile-role-label">{p.is_support ? "Support" : p.role === "company" ? "Company" : "Technician"}</p>
        <h1 className="mt-1 text-3xl font-extrabold tracking-tight flex items-center gap-3 flex-wrap" data-testid="profile-name">{p.name}{!p.is_support && <VipBadge user={p} testId="profile-vip-badge" />}</h1>
        {p.headline && !p.is_support && <p className="text-slate-600 mt-1">{p.headline}</p>}
        {!p.is_support && <>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
          {p.location && <span className="flex items-center gap-1"><MapPin size={14} />{p.location}</span>}
          {p.experience_years != null && p.role === "technician" && <span className="flex items-center gap-1"><Clock size={14} />{p.experience_years} years of experience</span>}
          {p.languages && <span className="flex items-center gap-1"><Languages size={14} />{p.languages}</span>}
          {p.connections_count != null && <span className="flex items-center gap-1" data-testid="profile-connections-count"><Users size={14} />{p.connections_count} connection{p.connections_count === 1 ? "" : "s"}</span>}
          {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-sky-700 hover:underline"><Globe size={14} />{p.website.replace(/^https?:\/\//, "")}</a>}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {(p.specializations || []).map((s) => <SpecPill key={s} spec={s} />)}
          {p.irata_level && <Pill cls="bg-slate-900 text-white border-slate-900">IRATA L{p.irata_level}</Pill>}
          <AvailabilityPill value={p.availability} />
        </div>
        </>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export default function UserProfile() {
  const { id } = useParams();
  const api = useApi();
  const nav = useNavigate();
  const { me } = useMe();
  const [p, setP] = useState(null);

  useEffect(() => { api("get", `/users/${id}`).then(setP).catch(() => { toast.error("Profile not found"); nav("/people"); }); }, [id, api, nav]);
  if (!p) return <Loader />;

  const conn = p.connection?.status || "none";
  const connect = async (method, url, msg) => {
    if (method === "delete" && conn === "connected" && !window.confirm(`Remove ${p.name} from your network?`)) return;
    try {
      const r = await api(method, url);
      const delta = r.status === "connected" ? 1 : conn === "connected" ? -1 : 0;
      setP({ ...p, connection: { status: r.status }, connections_count: (p.connections_count || 0) + delta });
      toast.success(msg);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const isMe = p.user_id === me.user_id;
  const endorse = async (skill) => {
    try {
      const { endorsed } = await api("post", `/users/${p.user_id}/endorse`, { text: skill });
      const e = p.endorsements || { counts: {}, mine: [] };
      setP({ ...p, endorsements: {
        counts: { ...e.counts, [skill]: (e.counts[skill] || 0) + (endorsed ? 1 : -1) },
        mine: endorsed ? [...e.mine, skill] : e.mine.filter((x) => x !== skill),
      } });
    } catch (err) { toast.error(errMsg(err)); }
  };
  const connButton = {
    none: <Button variant="outline" onClick={() => connect("post", `/connections/${p.user_id}`, "Invitation sent")} data-testid="connect-button"><UserPlus size={16} />Connect</Button>,
    pending_sent: <Button variant="outline" onClick={() => connect("delete", `/connections/${p.user_id}`, "Invitation withdrawn")} data-testid="connect-pending-button"><Clock size={16} />Invitation sent</Button>,
    pending_received: <Button variant="outline" className="border-sky-500 text-sky-700" onClick={() => connect("post", `/connections/${p.user_id}/accept`, "You are now connected")} data-testid="connect-accept-button"><UserCheck size={16} />Accept invitation</Button>,
    connected: <Button variant="outline" onClick={() => connect("delete", `/connections/${p.user_id}`, "Connection removed")} data-testid="connect-connected-button"><UserCheck size={16} />Connected</Button>,
  }[conn];

  return (
    <div className="space-y-4">
      <button onClick={() => nav(-1)} data-testid="profile-back-button" className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"><ArrowLeft size={16} />Back</button>
      <ProfileHeader p={p} actions={!isMe && (
        <>
          <Button onClick={() => nav(`/messages/${p.user_id}`)} data-testid="profile-message-button"><MessageSquare size={16} />{p.is_support ? "Message support" : "Message"}</Button>
          {!p.is_support && connButton}
          {p.cv_path && !p.is_support && <CvButton path={p.cv_path} filename={p.cv_filename} />}
        </>
      )} />
      {p.is_support ? (
        <div className="panel p-6" data-testid="support-help-text">
          <p className="eyebrow">Help</p>
          <p className="mt-2 text-slate-700">Questions about your account, jobs or VIP? Send us a message – we usually reply within 24 hours.</p>
        </div>
      ) : <ProfileView p={p} onEndorse={!isMe && conn === "connected" ? endorse : null} />}
      {p.role === "company" && (p.jobs || []).length > 0 && (
        <div>
          <h2 className="eyebrow mt-4 mb-3">Active jobs</h2>
          <div className="grid md:grid-cols-2 gap-4">{p.jobs.map((j, i) => <JobCard key={j.id} job={j} index={i} />)}</div>
        </div>
      )}
    </div>
  );
}
